import React, { useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useQuery } from '@tanstack/react-query';
import { apiService } from '../../services/apiService';
import { useAuth } from '../../hooks/useAuth';

const UsagePage = () => {
  const { user } = useAuth();
  const userKey = user?.userId || user?.id || 'anon';

  // Single aggregation query — backend computes totals, by-day series,
  // and recent-window counts in one MongoDB pipeline. Replaces the old
  // approach of fetching /memory/recent and computing client-side
  // (which capped silently at the fetch limit).
  const { data: stats, isLoading, error } = useQuery({
    queryKey: ['memory-stats', userKey],
    queryFn: () => apiService.getMemoryStats(),
    staleTime: 60000,
    enabled: !!user,
  });

  // Service metrics for the Avg-inject card. Same shape as the home page.
  const { data: metricsData } = useQuery({
    queryKey: ['tagging-metrics-usage'],
    queryFn: () => apiService.getTaggingMetrics(),
    staleTime: 30000,
    refetchInterval: 60000,
    enabled: !!user,
  });

  // Pack accept-rate aggregation, 30d window. Same source as home.
  const { data: packAgg } = useQuery({
    queryKey: ['pack-aggregate-usage', userKey, 720],
    queryFn: () => apiService.getPackAggregate({ hours: 720 }),
    staleTime: 60000,
    enabled: !!user,
  });

  const totalMemories = stats?.totalMemories ?? 0;
  const tokensSaved = stats?.totalTokens ?? 0;
  const recentCalls = stats?.memoriesLast30d ?? 0;
  const recent7d = stats?.memoriesLast7d ?? 0;

  const tokensDisplay = useMemo(() => {
    if (tokensSaved >= 1_000_000) return `${(tokensSaved / 1_000_000).toFixed(1)}M`;
    if (tokensSaved >= 1_000) return `${(tokensSaved / 1_000).toFixed(1)}k`;
    return String(tokensSaved);
  }, [tokensSaved]);

  // Rough dollar estimate at gpt-4o input pricing (~$2.50 / 1M tokens).
  const dollarEstimate = (tokensSaved / 1_000_000) * 2.5;

  // Inject latency series from /metrics — service-wide. The histogram
  // gives us real p50/p95/p99 estimates; sum/count is the avg fallback.
  const injectStats = useMemo(() => {
    const s = metricsData?.inject_duration?.success;
    if (!s || !s.count || s.count === 0) return null;
    return {
      avg: Math.round((s.sum / s.count) * 1000),
      p50: s.p50 ? Math.round(s.p50 * 1000) : null,
      p95: s.p95 ? Math.round(s.p95 * 1000) : null,
      p99: s.p99 ? Math.round(s.p99 * 1000) : null,
      count: s.count,
    };
  }, [metricsData]);
  const injectAvgMs = injectStats?.avg ?? null;

  // Pack acceptance — real, from /events/pack/aggregate.
  const acceptRatePct = useMemo(() => {
    if (!packAgg || !packAgg.total) return null;
    return Math.round(packAgg.acceptRate * 100);
  }, [packAgg]);

  // Cache hit ratio from /metrics — useful for "how warm is the
  // tag-graph propagation cache."
  const cacheHitRatio = useMemo(() => {
    const hits = metricsData?.cache_hits?.related ?? 0;
    const misses = metricsData?.cache_misses?.related ?? 0;
    const total = hits + misses;
    if (total === 0) return null;
    return Math.round((hits / total) * 100);
  }, [metricsData]);

  return (
    <>
      <Helmet><title>Usage · MME</title></Helmet>
      <div className="page">
        <div className="page-head">
          <h1>Usage</h1>
          <div className="sub">
            API calls, token economy, and SLO health across your workspace.
          </div>
        </div>

        {error && (
          <div className="error-banner">
            Could not load usage stats — the API may be temporarily unavailable.
          </div>
        )}

        <div className="kpi-row">
          <div className="kpi">
            <div className="lbl">Memories · 30d</div>
            <div className="val">{isLoading ? '—' : recentCalls.toLocaleString()}</div>
            <div className="delta">
              {totalMemories.toLocaleString()} total · {recent7d.toLocaleString()} this week
            </div>
          </div>
          <div className="kpi">
            <div className="lbl">Tokens stored</div>
            <div className="val">{isLoading ? '—' : tokensDisplay}</div>
            <div className="delta">content only</div>
          </div>
          <div className="kpi">
            <div className="lbl">Pack acceptance</div>
            <div className="val">
              {acceptRatePct == null ? '—' : <>{acceptRatePct}<em>%</em></>}
            </div>
            <div className="delta">
              {packAgg?.total
                ? `${packAgg.total.toLocaleString()} events · 30d`
                : 'no feedback yet · 30d'}
            </div>
          </div>
          <div className="kpi">
            <div className="lbl">Plan</div>
            <div className="val small">Free · beta</div>
            <div className="delta">unlimited during beta</div>
          </div>
        </div>

        <div className="two-col">
          <div className="panel">
            <div className="panel-head">
              <h3>Service metrics</h3>
              <span className="meta">
                {injectStats
                  ? `${injectStats.count.toLocaleString()} inject obs`
                  : 'live · /metrics'}
              </span>
            </div>
            <div className="panel-body" style={{ display: 'grid', gap: 14 }}>
              <div
                style={{
                  fontFamily: 'var(--mono)',
                  fontSize: 10,
                  color: 'var(--ink-4)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                }}
              >
                Inject latency
              </div>
              {['p50', 'p95', 'p99', 'avg'].map((k) => {
                const ms = injectStats?.[k];
                return (
                  <div key={k} className="lat-row">
                    <span className="k">{k}</span>
                    <div className="bar-wrap">
                      <div
                        style={{
                          width: `${ms == null ? 0 : Math.min(ms / 500, 1) * 100}%`,
                          background: (ms ?? 0) > 250 ? 'var(--accent)' : 'var(--ink)',
                        }}
                      />
                    </div>
                    <span className="v">{ms == null ? '—' : `${ms}ms`}</span>
                  </div>
                );
              })}
              <div
                style={{
                  fontFamily: 'var(--mono)',
                  fontSize: 10,
                  color: 'var(--ink-4)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  marginTop: 6,
                }}
              >
                Cache efficiency
              </div>
              <div className="lat-row">
                <span className="k">cache hit ratio</span>
                <div className="bar-wrap">
                  <div
                    style={{
                      width: `${cacheHitRatio == null ? 0 : cacheHitRatio}%`,
                      background: 'var(--ink)',
                    }}
                  />
                </div>
                <span className="v">
                  {cacheHitRatio == null ? '—' : `${cacheHitRatio}%`}
                </span>
              </div>
              <div className="lat-row">
                <span className="k">tokenizer cache</span>
                <div className="bar-wrap">
                  <div
                    style={{
                      width: `${(() => {
                        const h = metricsData?.tokenizer?.cache_hits ?? 0;
                        const m = metricsData?.tokenizer?.cache_misses ?? 0;
                        const t = h + m;
                        return t === 0 ? 0 : (h / t) * 100;
                      })()}%`,
                      background: 'var(--ink)',
                    }}
                  />
                </div>
                <span className="v">
                  {(() => {
                    const h = metricsData?.tokenizer?.cache_hits ?? 0;
                    const m = metricsData?.tokenizer?.cache_misses ?? 0;
                    const t = h + m;
                    return t === 0 ? '—' : `${Math.round((h / t) * 100)}%`;
                  })()}
                </span>
              </div>
              <div
                style={{
                  paddingTop: 8,
                  borderTop: '1px solid var(--line)',
                  fontSize: 12,
                  color: 'var(--ink-4)',
                  lineHeight: 1.55,
                }}
              >
                Percentiles estimated from a cumulative histogram of observed inject durations (10 buckets, 5ms — 5s). SLO is P95 ≤ 250ms; SLO guard auto-disables propagation for 60s on breach.
              </div>
            </div>
          </div>

          <div className="panel">
            <div className="panel-head">
              <h3>Token economy</h3>
              <span className="meta">all time</span>
            </div>
            <div className="panel-body">
              <div style={{ display: 'flex', gap: 24, alignItems: 'baseline', flexWrap: 'wrap' }}>
                <div>
                  <div
                    style={{
                      fontSize: 30,
                      fontWeight: 500,
                      letterSpacing: '-0.03em',
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {isLoading ? '—' : tokensSaved.toLocaleString()}
                  </div>
                  <div
                    style={{
                      fontFamily: 'var(--mono)',
                      fontSize: 11,
                      color: 'var(--ink-4)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.08em',
                      marginTop: 4,
                    }}
                  >
                    tokens stored
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 22, fontWeight: 500, color: 'var(--good)' }}>
                    ~${dollarEstimate.toFixed(2)}
                  </div>
                  <div
                    style={{
                      fontFamily: 'var(--mono)',
                      fontSize: 11,
                      color: 'var(--ink-4)',
                      marginTop: 4,
                    }}
                  >
                    at gpt-4o pricing
                  </div>
                </div>
              </div>
              <div
                style={{
                  marginTop: 20,
                  fontSize: 13,
                  color: 'var(--ink-3)',
                  lineHeight: 1.55,
                }}
              >
                Each pack respects its token budget exactly — MME deduplicates overlapping content and stops at the cap, on every call.
              </div>
            </div>
          </div>
        </div>

        {user?.orgId && (
          <p
            style={{
              marginTop: 24,
              fontSize: 12,
              color: 'var(--ink-4)',
              fontFamily: 'var(--mono)',
            }}
          >
            Org: {user.orgId}
          </p>
        )}
      </div>
    </>
  );
};

export default UsagePage;
